// /welcome — the first-run flow.
//
// Where /verify sends a brand-new account, invite or self-signup alike, since
// that is the one place every account becomes active. Because the TRIGGER is
// that navigation, nothing records whether someone has "done" onboarding: no
// column, no flag. Revisiting /welcome later is harmless, it just runs again.
//
// Up to three steps, all skippable:
//   1. A photo, through the same picker as /account.
//   2. How to reach you (docs/plan-notifications.md, step 5): push for this
//      browser, and, ONLY when the member has a verified phone, an UNCHECKED box
//      to have the text-signup kinds sent by text. Ticking it writes those SMS
//      cells as the member's own defaults (`textSignupSmsDefaults()`), which is
//      the whole "text signup template". Skipped when there is nothing to ask.
//   3. A short orientation: groups, RSVP, and the day-of "still in?" tap, which
//      is the one behaviour a new member would otherwise find surprising.
//
// ⚠ The SMS box lives HERE, after the number is verified, and never on
// /register. The registered A2P consent text describes exactly this order: the
// number is proven by a code first, then the member "chooses which
// notifications they want by text". /register is the screen carrier reviewers
// check; do not add controls to it (docs/a2p-campaign.md).
//
// Renders bare (see BARE_PATHS in chrome.ts). It carries its own way onward on
// every step, so hiding the tab bar here is not a trap.

import {
  ApiClientError,
  fetchMe,
  fetchNotificationSettings,
  listGroups,
  updateNotificationDefaults,
} from '../api/client.js';
import { navigate, type View } from '../router.js';
import { refreshChrome } from '../chrome.js';
import { el } from '../dom.js';
import { icon, type IconName } from '../icons.js';
import {
  NOTIF_TYPE_LABELS,
  textSignupSmsDefaults,
  type CurrentUser,
  type NotificationSettingsResponse,
} from '@msc/shared';
import { avatarPicker } from './avatarPicker.js';
import { enablePush, pushState, type PushState } from '../push.js';
import { isNative } from '../platform.js';
import { iosInstallHelp, isIosBrowserTab } from './installHelp.js';

export const welcomeView: View = async (outlet) => {
  const main = el('main', { class: 'auth-card welcome' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  let user: CurrentUser;
  let groupCount: number;
  // Settings and push state are optional: if either fails to load, the
  // notifications step is skipped rather than the whole flow.
  let settings: NotificationSettingsResponse | null = null;
  let push: PushState | null = null;
  try {
    const [me, groups] = await Promise.all([fetchMe(), listGroups()]);
    user = me;
    groupCount = groups.length;
    settings = await fetchNotificationSettings().catch(() => null);
    push = isNative() ? null : await pushState().catch(() => null);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    // Onboarding is a nicety. If it cannot load, the app itself is the better
    // place to be than an error on a screen the member never asked for.
    navigate('/', true);
    return;
  }

  // Native has no push sender yet (needs the APNs key), so the app only ever
  // asks about texts.
  const askPush =
    !!settings?.vapidPublicKey &&
    push?.supported === true &&
    !push.subscribed &&
    push.permission !== 'denied';
  const smsTypes = textSignupSmsDefaults();
  const askSms = settings?.reach.sms === null && smsTypes.length > 0;
  // An iPhone in a Safari tab cannot do push at all until the site is on the Home
  // Screen, so the step shows how (and is shown even when there is no text option).
  const askInstall = isIosBrowserTab() && !!settings?.vapidPublicKey;
  const hasNotifyStep = askPush || askSms || askInstall;
  const steps = hasNotifyStep ? 3 : 2;

  function progress(step: number): HTMLElement {
    return el('p', { class: 'muted small welcome-step' }, `Step ${step} of ${steps}`);
  }

  function photoStep(): void {
    const name = user.firstName ? `Welcome, ${user.firstName}` : 'Welcome';
    const next = el(
      'button',
      { type: 'button', class: user.avatarThumbUrl ? 'btn' : 'btn btn-secondary' },
      user.avatarThumbUrl ? 'Next' : 'Skip for now',
    );
    next.addEventListener('click', hasNotifyStep ? notifyStep : tourStep);

    main.replaceChildren(
      progress(1),
      el('h1', {}, name),
      el(
        'p',
        {},
        'Add a photo so the people in your groups know who is coming. You can change it any time under your account.',
      ),
      avatarPicker(user, (updated) => {
        user = updated;
        // The header avatar is cached; refresh it so it is right the moment
        // the member lands in the app.
        refreshChrome();
        photoStep();
      }),
      el('div', { class: 'welcome-actions' }, [next]),
    );
  }

  function notifyStep(): void {
    const current = settings;
    if (!current) {
      tourStep();
      return;
    }
    const error = el('p', { class: 'form-error', hidden: true });
    const sections: HTMLElement[] = [];

    if (askInstall) {
      sections.push(
        el('section', { class: 'welcome-choice' }, [
          el('h2', {}, 'Notifications on your iPhone'),
          iosInstallHelp(),
        ]),
      );
    }

    if (askPush) {
      const status = el('p', { class: 'muted small' }, '');
      const on = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Turn on push');
      // enablePush() must run inside the click: the browser's permission prompt
      // needs a user gesture.
      on.addEventListener('click', async () => {
        on.setAttribute('disabled', 'disabled');
        const message = await enablePush(current.vapidPublicKey);
        if (message) {
          status.textContent = message;
          on.removeAttribute('disabled');
          return;
        }
        status.textContent = 'Push is on for this browser.';
        on.hidden = true;
      });
      sections.push(
        el('section', { class: 'welcome-choice' }, [
          el('h2', {}, 'Push notifications'),
          el(
            'p',
            { class: 'muted small' },
            'Get a notification in this browser for new events and the day-of check-in. ' +
              'Push is per device, so do the same on your phone if you want it there.',
          ),
          on,
          status,
        ]),
      );
    }

    // Pre-ticked only on a REVISIT where they already said yes. A first visit
    // always starts unchecked, because ticking this box IS the opt-in.
    const alreadyOn = smsTypes.every((t) => current.defaults[t].sms?.enabled === true);
    const smsBox = el('input', { type: 'checkbox', id: 'welcome-sms' }) as HTMLInputElement;
    smsBox.checked = alreadyOn;

    if (askSms) {
      // An account made by text has no email address, so with this unchecked
      // and no push it hears about NOTHING. Say so rather than let it look fine.
      const noEmailHint = el(
        'p',
        { class: 'muted small', hidden: true },
        'Your account has no email address, so without texts or push you will not hear ' +
          'about new events or the day-of check-in.',
      );
      const syncHint = (): void => {
        noEmailHint.hidden = smsBox.checked || current.reach.email !== 'noAddress';
      };
      smsBox.addEventListener('change', syncHint);
      syncHint();

      sections.push(
        el('section', { class: 'welcome-choice' }, [
          el('h2', {}, 'Text messages'),
          el('label', { class: 'check welcome-check', for: 'welcome-sms' }, [
            smsBox,
            ' Text me these at the number I verified',
          ]),
          el(
            'ul',
            { class: 'welcome-sms-list muted small' },
            smsTypes.map((t) => el('li', {}, NOTIF_TYPE_LABELS[t].label)),
          ),
          // Mirrors the registered program terms (docs/a2p-campaign.md). Reword
          // only together with that file.
          el(
            'p',
            { class: 'muted small' },
            'Msg & data rates may apply. Message frequency varies with your groups. Reply STOP ' +
              'to opt out, HELP for help. Change this any time in Notifications settings.',
          ),
          noEmailHint,
        ]),
      );
    }

    const next = el('button', { type: 'button', class: 'btn' }, 'Next');
    next.addEventListener('click', async () => {
      error.hidden = true;
      if (askSms && smsBox.checked !== alreadyOn) {
        next.setAttribute('disabled', 'disabled');
        try {
          settings = await updateNotificationDefaults({
            cells: smsTypes.map((type) => ({ type, channel: 'sms' as const, enabled: smsBox.checked })),
          });
        } catch (err) {
          error.textContent =
            err instanceof ApiClientError ? err.message : 'Could not save that. Try again.';
          error.hidden = false;
          next.removeAttribute('disabled');
          return;
        }
      }
      tourStep();
    });

    const back = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Back');
    back.addEventListener('click', photoStep);

    main.replaceChildren(
      progress(2),
      el('h1', {}, 'How should we reach you?'),
      ...sections,
      error,
      el('div', { class: 'welcome-actions' }, [back, next]),
    );
  }

  function tourStep(): void {
    const point = (name: IconName, title: string, text: string): HTMLElement =>
      el('li', { class: 'welcome-point' }, [
        el('span', { class: 'welcome-icon', 'aria-hidden': 'true' }, [icon(name)]),
        el('div', {}, [el('strong', {}, title), el('p', { class: 'muted small' }, text)]),
      ]);

    const done = el('button', { type: 'button', class: 'btn' }, groupCount ? 'Go to my events' : 'Find a group');
    done.addEventListener('click', () => navigate(groupCount ? '/' : '/groups', true));

    const back = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Back');
    back.addEventListener('click', hasNotifyStep ? notifyStep : photoStep);

    main.replaceChildren(
      progress(steps),
      el('h1', {}, 'How it works'),
      el('ul', { class: 'welcome-points' }, [
        point(
          'groups',
          'Your groups',
          'Poker night, book club, the Saturday ride. Each group has its own feed, events and members, under the Groups tab.',
        ),
        point(
          'events',
          'Answer each event',
          'Tap Going, Maybe or Not going. The organizer sees the headcount as people answer.',
        ),
        point(
          'calendar',
          'On the day: still in?',
          'If you said Going ahead of time, we ask again on the day. One tap keeps the count honest, so nobody sets up for six and gets four.',
        ),
      ]),
      el('div', { class: 'welcome-actions' }, [back, done]),
    );
  }

  photoStep();
};
